import React, { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Loader } from 'components/Shared/Loader'
import { EditUserAccountForm } from 'components/Users/EditUserForm/EditUserAccountForm'
import internalStyles from 'components/Users/Details/UserDetails.module.css'
import { ReadOnlyTextField } from 'components/Shared/ReadOnlyTextField'
import { HTTP_404_ERROR } from 'Constants'
import { useErrorHandling } from 'context/errorHandling'
import { DetailsColumn, DetailsRow } from 'components/Shared/CrudComponents'
import Sections from 'components/Users/sections'
import Module from 'constants/customFieldModules'
import { CustomFieldsProvider } from 'context/customFields'
import { UserNotificationsSettings } from 'components/Users/Details/UserNotificationsSettings'
import { useAuth, USER_DATA_READ, USER_EDIT } from 'context/auth'
import { EXTRA, MAIN } from 'components/Groups/GroupsForm/MembershipType'

export function UserDetails ({
  fetchUserData,
  fetchUserGroupMembershipsData,
  title,
  showPasswordChangeForm,
  disableAccountFieldsHelperTextMargin,
  showNotificationsSettings
}) {
  const [user, setUser] = useState(null)
  const [userGroupMemberships, setUserGroupMemberships] = useState(null)
  const { hasAnyPrivileges } = useAuth()

  const history = useHistory()
  const { handleError } = useErrorHandling()

  const handleNotFoundError = useCallback((error, message) =>
      handleError(error, {
          [HTTP_404_ERROR]: message
        }
      ).then(() => history.push('/users'))
    , [handleError, history])

  useEffect(() => {
    fetchUserData().then(response => {
        setUser(response)
      },
    ).catch(error => handleNotFoundError(error, 'notifications.error.userFetchError'))
  }, [fetchUserData, handleNotFoundError])

  useEffect(() => {
    fetchUserGroupMembershipsData()
      .then(setUserGroupMemberships)
      .catch(handleError)
  }, [fetchUserGroupMembershipsData, handleError])

  const mainGroup = userGroupMemberships
    ?.find(membership => membership.membershipType === MAIN)
  const extraGroups = userGroupMemberships
    ?.filter(membership => membership.membershipType === EXTRA)

  return <div>
    {user ?
      <>
        {title(user, !hasAnyPrivileges([USER_EDIT]))}
        {hasAnyPrivileges([USER_DATA_READ]) ?
        <CustomFieldsProvider module={Module.Users} entityId={user?.id}>
          <DetailsRow>
            <DetailsColumn section={Sections.BasicDetails} visible={hasAnyPrivileges([USER_DATA_READ])}>
              <ReadOnlyTextField clearDisabled label={'user.details.firstName'} value={user.firstName}/>
              <ReadOnlyTextField clearDisabled label={'user.details.lastName'} value={user.lastName}/>
              <ReadOnlyTextField clearDisabled label={'user.details.phoneNumber'} value={user.phoneNumber}/>
              <ReadOnlyTextField clearDisabled label={'user.details.personalPhoneNumber'}
                                 value={user.personalPhoneNumber}/>
              <ReadOnlyTextField clearDisabled label={'user.details.email'} value={user.email}/>
              <ReadOnlyTextField clearDisabled label={'user.details.mainGroup'} value={mainGroup?.name}/>
              {extraGroups?.map((group, index) =>
                <ReadOnlyTextField
                  key={index}
                  label={'user.details.extraGroup'}
                  value={group.name}
                  additionalLabelText={index + 1}
                />
              )}

              {extraGroups?.length === 0 && <ReadOnlyTextField label={'user.details.extraGroup'}/>}

              <ReadOnlyTextField clearDisabled label={'user.details.hash'} value={user.hash}/>
              <ReadOnlyTextField clearDisabled label={'user.details.externalId'} value={user.externalId}/>
            </DetailsColumn>
            {showPasswordChangeForm &&
              <DetailsColumn section={{ label: 'form.account.title' }}>
                <EditUserAccountForm
                  className={internalStyles.editUserAccountContainer}
                  user={user}
                  disableAccountFieldsHelperTextMargin={disableAccountFieldsHelperTextMargin}/>
              </DetailsColumn>
            }{showNotificationsSettings &&
            <DetailsColumn section={{ label: 'user.emailNotificationsSettings.title' }}>
              <UserNotificationsSettings user={user}/>
            </DetailsColumn>
          }
          </DetailsRow>
        </CustomFieldsProvider>
          : <></>
        }
      </>
      :
      <Loader/>
    }
  </div>
}
